import type { Locale } from './types'

/**
 * A flag for the language selector.
 *
 * ## Simplified, and said so here
 *
 * These are **not** faithful renderings. Mongolia's Soyombo is a stack of ten elements with
 * exact proportions defined in law; what is drawn here is a recognisable reduction — the
 * flame, the sun and moon, the two triangles and the two bars — at a size where the real
 * thing would be a smudge. The Union Jack's saltires are drawn without the correct
 * counterchange offset for the same reason.
 *
 * They are SVG rather than the emoji flags (🇲🇳 🇬🇧) on purpose: emoji flags do not render on
 * Windows, where Chrome falls back to the two letters "MN" and "GB". A control that shows a
 * flag on a Mac and two letters on a PC is a control that was not designed.
 *
 * If the Foundation would rather use exact artwork, these are two files to replace and
 * nothing else changes.
 */

const FLAGS: Record<Locale, { title: string; svg: React.ReactNode }> = {
  // The Union Jack, for English. Blue field, white then red saltires, white then red cross.
  en: {
    title: 'United Kingdom',
    svg: (
      <>
        <rect width="24" height="16" fill="#012169" />
        <path d="M0 0 24 16M24 0 0 16" stroke="#FFF" strokeWidth="3.2" />
        <path d="M0 0 24 16M24 0 0 16" stroke="#C8102E" strokeWidth="1.8" />
        <path d="M12 0v16M0 8h24" stroke="#FFF" strokeWidth="5.4" />
        <path d="M12 0v16M0 8h24" stroke="#C8102E" strokeWidth="3.2" />
      </>
    ),
  },
  // Mongolia: red, blue, red vertical thirds, with the Soyombo on the hoist band.
  mn: {
    title: 'Mongolia',
    svg: (
      <>
        <rect width="8" height="16" fill="#C4272F" />
        <rect x="8" width="8" height="16" fill="#015197" />
        <rect x="16" width="8" height="16" fill="#C4272F" />
        <g fill="#F9CF02">
          {/* flame */}
          <path d="M4 1.7c.5.7.2 1.2 0 1.6-.2-.4-.5-.9 0-1.6Z" />
          {/* sun, then moon */}
          <circle cx="4" cy="4.2" r="0.55" />
          <path d="M3.45 5.5h1.1v0.5h-1.1z" />
          {/* the two triangles, pointing in */}
          <path d="M4 6.4 4.9 7.9H3.1Z" />
          <path d="M4 13.6 3.1 12.1h1.8Z" />
          {/* the two bars */}
          <rect x="2.7" y="8.3" width="2.6" height="0.5" />
          <rect x="2.7" y="11.2" width="2.6" height="0.5" />
          {/* the two fish, reduced to the yin-yang they enclose */}
          <path d="M4 9.2a0.9 0.9 0 0 1 0 1.8 0.9 0.9 0 0 1 0-1.8Z" />
          <circle cx="4" cy="9.65" r="0.18" fill="#C4272F" />
        </g>
      </>
    ),
  },
}

export interface FlagProps {
  locale: Locale
  className?: string
}

export function Flag({ locale, className }: FlagProps) {
  const flag = FLAGS[locale]
  return (
    <svg
      className={className ? `ul-flag ${className}` : 'ul-flag'}
      viewBox="0 0 24 16"
      width="24"
      height="16"
      /*
       * `aria-hidden` and NO `role="img"`. The two together were contradictory — a role
       * saying "this is an image, announce it" on an element removed from the accessibility
       * tree — and the selector already names the language in words beside every flag, so a
       * screen reader saying "United Kingdom, English" is the same thing twice.
       *
       * `<title>` stays: with the svg hidden from assistive tech it is doing one job, which
       * is the native tooltip on hover.
       */
      aria-hidden="true"
      focusable="false"
    >
      <title>{flag.title}</title>
      {flag.svg}
    </svg>
  )
}
